// Student glossary: immutable grouping of existing terminology records.
// Hide incomplete rows, combine category aliases and preserve differing text examples.
// The authenticated route, stored data and in-reader term matching are unchanged.
import React, { useState, useEffect, useMemo } from 'react';
import { IconSearch, IconX, IconBookOpen } from '../components/icons.jsx';
import { groupTerminology, terminologyCategories, filterTerminology } from '../components/terminologyView.js';
import TerminologyCard from '../components/TerminologyCard.jsx';
import { track } from '../analytics/analytics.js';

export default function TerminologyPage({ state }) {
    const terms = useMemo(() => groupTerminology(state?.terminology), [state?.terminology]);
    const categories = useMemo(() => terminologyCategories(terms), [terms]);

    const [query, setQuery] = useState('');
    const [category, setCategory] = useState('All');
    const [fontSize, setFontSize] = useState(() => {
        try { return localStorage.getItem('btl_v2_terminologyFontSize') || 'small'; }
        catch { return 'small'; }
    });
    useEffect(() => {
        try { localStorage.setItem('btl_v2_terminologyFontSize', fontSize); } catch { /* ignore */ }
    }, [fontSize]);
    const cardScaleClass = fontSize === 'medium' ? 'reading-scale-medium'
        : fontSize === 'large' ? 'reading-scale-large'
        : '';

    const counts = useMemo(() => {
        const c = { All: terms.length };
        categories.forEach((cat) => { c[cat] = terms.filter((t) => t.categories.includes(cat)).length; });
        return c;
    }, [terms, categories]);

    const filtered = useMemo(() => {
        return filterTerminology(terms, query, category);
    }, [terms, query, category]);

    return (
        <div className="page-fade-in min-h-[calc(100vh-4rem)] bg-book-bg paper-texture text-book-text">
            <div className="max-w-7xl mx-auto px-4 md:px-8 py-10 md:py-16">
                {/* Header */}
                <div className="mb-10 flex items-start justify-between gap-4 flex-wrap">
                    <div>
                        <div className="font-display text-book-accent text-xs mb-2 font-semibold">Glossary</div>
                        <h1 className="font-display text-3xl md:text-5xl text-book-text mb-2 leading-tight">Literary Terminology</h1>
                        <p className="font-serif italic text-book-muted text-lg">Essential terms for IGCSE English Language &amp; Literature.</p>
                        <p className="font-serif text-book-muted text-sm mt-3">{terms.length} terms and concepts, with text examples and exam tips. Open a card’s extra examples to compare how a term works in different contexts.</p>
                    </div>
                    {/* Font size control */}
                    <div className="flex flex-col items-end gap-1 flex-shrink-0">
                        <span className="font-display text-book-muted text-xs font-semibold">Card font size</span>
                        <div className="flex items-center border border-book-accent/40 rounded-sm overflow-hidden">
                            {[['small', 11, '100%'], ['medium', 14, '115%'], ['large', 17, '130%']].map(([k, px, label]) => {
                                const active = fontSize === k;
                                return (
                                    <button key={k} onClick={() => setFontSize(k)}
                                        title={`${k} · ${label}`}
                                        className={`px-2 leading-none flex items-center justify-center transition-colors ${active ? 'bg-book-accent text-black' : 'text-book-muted hover:text-book-accent hover:bg-book-accent/10'}`}
                                        style={{ height: 30, minWidth: 28 }}>
                                        <span className="font-display font-bold" style={{ fontSize: px }}>A</span>
                                    </button>
                                );
                            })}
                        </div>
                    </div>
                </div>

                {/* Search bar */}
                <div className="relative mb-8 max-w-xl">
                    <IconSearch className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-book-muted" />
                    <input type="text" value={query} onChange={(e) => setQuery(e.target.value)} onBlur={() => { if (query.trim()) track('terminology_search'); }}
                        placeholder="Search terms, definitions, examples…"
                        className="w-full bg-book-pageDark border border-book-accent/30 pl-11 pr-10 py-3 text-book-text font-serif focus:outline-none focus:border-book-accent" />
                    {query && (
                        <button onClick={() => setQuery('')} className="absolute right-3 top-1/2 -translate-y-1/2 text-book-muted hover:text-book-accent">
                            <IconX className="w-4 h-4" />
                        </button>
                    )}
                </div>

                {/* grid-cols-1 base is REQUIRED on mobile: a bare `grid` with no column
                    template makes an implicit `auto` column that sizes to the widest item's
                    max-content -- here the nowrap category bar (~1131px) -- which blew the whole
                    page far wider than the phone (zoom-out/cramped). grid-cols-1 = minmax(0,1fr)
                    clamps the column to the viewport so the bar scrolls internally instead. */}
                <div className="grid grid-cols-1 md:grid-cols-[220px_1fr] gap-6">
                    {/* Category sidebar */}
                    <aside className="md:sticky md:top-24 self-start">
                        {/* Heading: desktop sidebar only (the mobile tab bar mirrors the
                            Account page, which has no label above its tabs). */}
                        <div className="hidden md:block font-display text-book-accent text-xs mb-3 font-semibold">Categories</div>

                        {/* MOBILE (below md): an Account-style horizontal tab bar -- bottom
                            border, slide-scroll, and an active underline indicator (NOT the
                            sidebar's left-border/filled-box chips). Right-edge fade = scroll cue. */}
                        <div className="relative md:hidden">
                            <div className="flex border-b border-book-accent/30 overflow-x-auto no-scrollbar whitespace-nowrap">
                                {['All', ...categories].map((cat) => {
                                    const isActive = cat === category;
                                    return (
                                        <button key={cat} onClick={() => { setCategory(cat); track('terminology_category'); }}
                                            className={`flex-shrink-0 px-3 py-3 font-display text-xs transition-colors relative font-semibold ${isActive ? 'text-book-accent' : 'text-book-muted hover:text-book-accent'}`}>
                                            <span className="inline-flex items-center gap-2">
                                                {cat}
                                                <span className="font-serif text-xs opacity-70 font-semibold text-book-muted">{counts[cat] ?? 0}</span>
                                            </span>
                                            {isActive && <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-book-accent"></div>}
                                        </button>
                                    );
                                })}
                            </div>
                            <div className="btl-edge-fade pointer-events-none absolute top-0 bottom-0 right-0 w-10 bg-gradient-to-l from-book-bg to-transparent"></div>
                        </div>

                        {/* DESKTOP (md+): the original vertical 220px sidebar, unchanged. */}
                        <div className="hidden md:flex md:flex-col gap-1">
                            {['All', ...categories].map((cat) => {
                                const isActive = cat === category;
                                return (
                                    <button key={cat} onClick={() => { setCategory(cat); track('terminology_category'); }}
                                        className={`text-left px-3 py-2 font-display text-xs transition-colors border-l-2 flex items-center justify-between gap-3 whitespace-nowrap font-medium rounded-full ${isActive ? 'bg-book-accent/10 border-book-accent text-book-accent' : 'border-transparent text-book-muted hover:text-book-accent hover:border-book-accent/40'}`}>
                                        <span>{cat}</span>
                                        <span className="font-serif text-xs opacity-70 font-semibold text-book-muted">{counts[cat] ?? 0}</span>
                                    </button>
                                );
                            })}
                        </div>
                    </aside>

                    {/* Cards (min-w-0 so this grid item can shrink to the column instead of
                        forcing its content's min-content width back onto the page) */}
                    <div className="min-w-0">
                        {filtered.length === 0 ? (
                            <div className="border border-book-accent/20 bg-book-pageDark/40 p-10 text-center">
                                <IconBookOpen className="w-10 h-10 mx-auto mb-3 text-book-accent/40" />
                                <p className="font-serif italic text-book-muted">No terms match this search.</p>
                            </div>
                        ) : (
                            <div className={`grid grid-cols-1 sm:grid-cols-2 gap-4 ${cardScaleClass}`}>
                                {filtered.map((group) => <TerminologyCard key={group.id} group={group} packs={state?.packs} />)}
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
}
